import { Component } from '@theme/component';

/**
 * Gourmet Pantry delivery pin (snippets/gp-delivery-pin.liquid).
 *
 * Saves the customer's delivery location as the cart attribute named in data-attribute,
 * as a Google Maps link, so it reaches the order. The location comes from a Google map
 * (when data-map-key holds a Maps JavaScript API key), the device's location, or a pasted
 * Google Maps link.
 *
 * The cart re-renders its markup on every change, and that morph would empty the map
 * container, so the map lives in an element this component owns and re-attaches after
 * each update.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - Collapsible wrapper.
 * @property {HTMLElement} [map] - Map container, present only with an API key.
 * @property {HTMLInputElement} linkInput - Pasted Google Maps link.
 * @property {HTMLButtonElement} removeButton - Clears the pin.
 * @property {HTMLAnchorElement} viewLink - Opens the saved pin in Google Maps.
 * @property {HTMLElement} status - Live region for results and errors.
 * @property {HTMLElement} [badge] - "Pin set" marker in the summary.
 *
 * @extends {Component<Refs>}
 */
class GpDeliveryPin extends Component {
  requiredRefs = ['details', 'linkInput', 'removeButton', 'viewLink', 'status'];

  /** Dubai, the delivery area, for an empty map. */
  static #DEFAULT_CENTER = { lat: 25.2048, lng: 55.2708 };
  static #MAPS_LINK = /^https:\/\/((www\.)?google\.[a-z.]+\/maps|maps\.google\.[a-z.]+|maps\.app\.goo\.gl|goo\.gl\/maps)([/?#]|$)/i;

  /** @type {Promise<void> | undefined} */
  static #mapsLoading;

  #value = '';
  /** @type {HTMLDivElement | undefined} */
  #mapHost;
  /** @type {any} */
  #map;
  /** @type {any} */
  #marker;
  /** @type {AbortController | undefined} */
  #saveController;
  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    super.connectedCallback();
    this.#value = this.dataset.value ?? '';
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    this.refs.details.addEventListener('toggle', () => this.#onToggle(), { signal: this.#listeners.signal });
    this.#render();
    this.#reattachMap();
    if (this.refs.details.open) this.#ensureMap();
  }

  updatedCallback() {
    super.updatedCallback();
    // The server's copy of the attribute wins unless a save is still in flight.
    if (!this.#saveController) this.#value = this.dataset.value ?? this.#value;
    this.#render();
    this.#reattachMap();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#saveController?.abort();
  }

  /** Fills the pin from the device's location. */
  useLocation() {
    if (!('geolocation' in navigator)) {
      this.#announce(this.dataset.msgLocationError);
      return;
    }

    this.#announce(this.dataset.msgLocating);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.#setPoint({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      () => this.#announce(this.dataset.msgLocationError),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }

  /** Saves a pasted Google Maps link. */
  saveLink() {
    const link = this.refs.linkInput.value.trim();
    if (!GpDeliveryPin.#MAPS_LINK.test(link)) {
      this.#announce(this.dataset.msgInvalid);
      this.refs.linkInput.focus();
      return;
    }

    const point = GpDeliveryPin.#parsePoint(link);
    if (point) this.#showPoint(point);
    this.refs.linkInput.value = '';
    this.#save(link);
  }

  /** Hides the map when Google rejects the API key; the other options keep working. */
  mapFailed() {
    if (this.refs.map) this.refs.map.hidden = true;
    this.#announce(this.dataset.msgMapError);
  }

  /** Clears the pin. */
  removePin() {
    this.#marker?.setMap(null);
    this.#marker = undefined;
    this.#save('');
  }

  #onToggle() {
    if (this.refs.details.open) this.#ensureMap();
  }

  /** @param {{ lat: number, lng: number }} point */
  #setPoint(point) {
    this.#showPoint(point);
    this.#save(`https://www.google.com/maps?q=${point.lat.toFixed(6)},${point.lng.toFixed(6)}`);
  }

  /** @param {{ lat: number, lng: number }} point */
  #showPoint(point) {
    if (!this.#map) return;
    this.#placeMarker(point);
    this.#map.panTo(point);
    if (this.#map.getZoom() < 15) this.#map.setZoom(17);
  }

  /** @param {{ lat: number, lng: number }} point */
  #placeMarker(point) {
    const google = /** @type {any} */ (window).google;
    if (this.#marker) {
      this.#marker.setPosition(point);
      return;
    }
    this.#marker = new google.maps.Marker({ map: this.#map, position: point, draggable: true });
    this.#marker.addListener('dragend', (/** @type {any} */ event) => this.#setPoint(event.latLng.toJSON()));
  }

  async #ensureMap() {
    const key = this.dataset.mapKey;
    const container = this.refs.map;
    if (!key || !container || this.#map) return;

    try {
      await GpDeliveryPin.#loadMaps(key);
      const google = /** @type {any} */ (window).google;
      const { Map } = await google.maps.importLibrary('maps');
      if (this.#map) return;

      this.#mapHost = document.createElement('div');
      this.#mapHost.className = 'gp-pin__map-canvas';
      this.#reattachMap();

      const point = GpDeliveryPin.#parsePoint(this.#value);
      this.#map = new Map(this.#mapHost, {
        center: point ?? GpDeliveryPin.#DEFAULT_CENTER,
        zoom: point ? 17 : 11,
        disableDefaultUI: true,
        zoomControl: true,
        clickableIcons: false,
        gestureHandling: 'cooperative',
      });
      this.#map.addListener('click', (/** @type {any} */ event) => this.#setPoint(event.latLng.toJSON()));
      if (point) this.#placeMarker(point);
    } catch {
      container.hidden = true;
      this.#announce(this.dataset.msgMapError);
    }
  }

  /** Puts the map back after a cart re-render emptied its container. */
  #reattachMap() {
    const container = this.refs.map;
    if (this.#mapHost && container && this.#mapHost.parentElement !== container) {
      container.append(this.#mapHost);
    }
  }

  /** @param {string} value */
  async #save(value) {
    this.#value = value;
    this.#render();

    this.#saveController?.abort();
    const controller = new AbortController();
    this.#saveController = controller;
    const attribute = this.dataset.attribute ?? 'Delivery location';
    const theme = /** @type {any} */ (window).Theme;
    const url = `${theme?.routes?.cart_update_url ?? '/cart/update'}.js`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [attribute]: value } }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
      this.dataset.value = value;
      this.#announce(value ? this.dataset.msgSaved : this.dataset.msgRemoved);
    } catch {
      if (controller.signal.aborted) return;
      this.#announce(this.dataset.msgError);
    } finally {
      if (this.#saveController === controller) this.#saveController = undefined;
    }
  }

  #render() {
    const hasPin = this.#value !== '';
    this.refs.removeButton.hidden = !hasPin;
    this.refs.viewLink.hidden = !hasPin;
    this.refs.viewLink.href = hasPin ? this.#value : '#';
    if (this.refs.badge) this.refs.badge.hidden = !hasPin;
  }

  /** @param {string | undefined} message */
  #announce(message) {
    this.refs.status.textContent = message ?? '';
  }

  /**
   * Reads coordinates from a Google Maps link (?q=lat,lng or @lat,lng). Short links
   * (maps.app.goo.gl) carry none; they are saved as they are.
   * @param {string} value
   * @returns {{ lat: number, lng: number } | null}
   */
  static #parsePoint(value) {
    const match =
      value.match(/[?&](?:q|query|ll)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/) ??
      value.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
    if (!match) return null;
    const lat = Number(match[1]);
    const lng = Number(match[2]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }

  /**
   * Loads the Maps JavaScript API once per page.
   * @param {string} key
   * @returns {Promise<void>}
   */
  static #loadMaps(key) {
    const win = /** @type {any} */ (window);
    if (win.google?.maps?.importLibrary) return Promise.resolve();

    GpDeliveryPin.#mapsLoading ??= new Promise((resolve, reject) => {
      const callback = '__gpMapsReady';
      win[callback] = () => resolve();
      // Google calls this global when the key is invalid or not allowed on this domain.
      win.gm_authFailure = () => {
        for (const pin of document.querySelectorAll('gp-delivery-pin')) {
          if (pin instanceof GpDeliveryPin) pin.mapFailed();
        }
      };
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&v=weekly&callback=${callback}`;
      script.async = true;
      script.onerror = () => {
        GpDeliveryPin.#mapsLoading = undefined;
        reject(new Error('Google Maps failed to load'));
      };
      document.head.append(script);
    });
    return GpDeliveryPin.#mapsLoading;
  }
}

if (!customElements.get('gp-delivery-pin')) {
  customElements.define('gp-delivery-pin', GpDeliveryPin);
}
